import { useState, useCallback } from 'react';

import { api } from '@/lib/api';
import { fanficsCatalogApi, type FanficsCatalogType } from '@/lib/fanfics-catalog-api';

interface Universe {
  id: number;
  title: string;
  sec_title: string;
  slug: string;
  fanfic_cnt: number;
  fandoms: Array<{
    id: number;
    title: string;
    sec_title: string;
    slug: string;
    is_universe: boolean;
  }>;
}

interface UniverseSearchResult {
  universes: Universe[];
  hasNextPage: boolean;
  hasPrevPage: boolean;
  limit: number;
  nextPage: number | null;
  page: number;
  totalDocs: number;
  totalPages: number;
}

interface UseUniversesOptions {
  useNewApi?: boolean;
  type?: FanficsCatalogType;
}

/** Paginated result for infinite scroll dropdowns */
export interface PaginatedUniverseResult {
  items: { id: string; name: string; count: number }[];
  hasNextPage: boolean;
}

const DROPDOWN_LIMIT = 50;

export function useUniverses(options: UseUniversesOptions = {}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { useNewApi = false, type = 'fanfic' } = options;

  const searchUniverses = useCallback(
    async (query: string): Promise<{ id: string; name: string }[]> => {
      setLoading(true);
      setError(null);

      try {
        const encodedQuery = encodeURIComponent(query);
        const requestPath = `/api/universes-custom?search=${encodedQuery}&limit=10${
          useNewApi ? `&type=${type}` : ''
        }`;
        const response = await (useNewApi ? fanficsCatalogApi : api).get<UniverseSearchResult>(
          requestPath,
        );

        const results = response.data.universes.map((universe) => ({
          id: universe.id.toString(),
          name: universe.title || universe.sec_title || '',
          count: universe.fanfic_cnt || 0,
        }));

        return results;
      } catch (err) {
        console.error('Error searching universes:', err);
        setError(err instanceof Error ? err.message : 'Failed to search universes');
        return [];
      } finally {
        setLoading(false);
      }
    },
    [type, useNewApi],
  );

  /** Paginated search for infinite scroll dropdowns */
  const searchUniversesPaginated = useCallback(
    async (query: string, page: number = 1): Promise<PaginatedUniverseResult> => {
      try {
        const encodedQuery = encodeURIComponent(query);
        const requestPath = `/api/universes-custom?search=${encodedQuery}&page=${page}&limit=${DROPDOWN_LIMIT}${
          useNewApi ? `&type=${type}` : ''
        }`;
        const response = await (useNewApi ? fanficsCatalogApi : api).get<UniverseSearchResult>(
          requestPath,
        );
        const items = response.data.universes.map((universe) => ({
          id: universe.id.toString(),
          name: universe.title || universe.sec_title || '',
          count: universe.fanfic_cnt || 0,
        }));

        return { items, hasNextPage: response.data.hasNextPage };
      } catch (err) {
        console.error('Error searching universes paginated:', err);
        return { items: [], hasNextPage: false };
      }
    },
    [type, useNewApi],
  );

  const getUniverses = useCallback(
    async (page: number = 1, limit: number = 10): Promise<UniverseSearchResult> => {
      setLoading(true);
      setError(null);

      try {
        const requestPath = `/api/universes-custom?page=${page}&limit=${limit}${
          useNewApi ? `&type=${type}` : ''
        }`;
        const response = await (useNewApi ? fanficsCatalogApi : api).get<UniverseSearchResult>(
          requestPath,
        );
        return response.data;
      } catch (err) {
        console.error('Error fetching universes:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch universes');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [type, useNewApi],
  );

  /** Get universes with paginated result for infinite scroll dropdowns */
  const getUniversesPaginated = useCallback(
    async (page: number = 1): Promise<PaginatedUniverseResult> => {
      try {
        const requestPath = `/api/universes-custom?page=${page}&limit=${DROPDOWN_LIMIT}${
          useNewApi ? `&type=${type}` : ''
        }`;
        const response = await (useNewApi ? fanficsCatalogApi : api).get<UniverseSearchResult>(
          requestPath,
        );
        const items = response.data.universes.map((universe) => ({
          id: universe.id.toString(),
          name: universe.title || universe.sec_title || '',
          count: universe.fanfic_cnt || 0,
        }));

        return { items, hasNextPage: response.data.hasNextPage };
      } catch (err) {
        console.error('Error fetching universes paginated:', err);
        return { items: [], hasNextPage: false };
      }
    },
    [type, useNewApi],
  );

  return {
    searchUniverses,
    searchUniversesPaginated,
    getUniverses,
    getUniversesPaginated,
    loading,
    error,
  };
}
